import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import { Coat, CoatStatus, CoatType, CoatCondition, CoatSize } from "./types/api"

// Currency formatting
export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// Formatting utilities

// Format phone number for DISPLAY ONLY (Sri Lankan format)
export function formatPhoneNumber(phone: string): string {
  const cleaned = phone.replace(/\D/g, '');
  
  // Sri Lankan format: 077 123 4567 or +94 77 123 4567
  if (cleaned.startsWith('94')) {
    // International format: +94 77 123 4567
    const match = cleaned.match(/^(94)(\d{2})(\d{3})(\d{4})$/);
    if (match) {
      return `+${match[1]} ${match[2]} ${match[3]} ${match[4]}`;
    }
  } else if (cleaned.length === 10) {
    // Local format: 077 123 4567
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return `${match[1]} ${match[2]} ${match[3]}`;
    }
  }
  
  // Fallback: return cleaned digits
  return cleaned;
}

// Normalize phone to digits-only for STORAGE
export function normalizePhoneNumber(phone: string): string {
  // Remove all non-digit characters
  return phone.replace(/\D/g, '');
}

export function formatPrice(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'LKR',
    minimumFractionDigits: 2,
  }).format(amount);
}

// Validation utilities

export function validatePhoneNumber(phone: string): boolean {
  const cleaned = phone.replace(/\D/g, '');
  return /^\d{10,15}$/.test(cleaned);
}

export function validateEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

// Coat utilities

export function getCoatStatusColor(status: CoatStatus): string {
  switch (status) {
    case 'available':
      return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
    case 'rented':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
    case 'cleaning':
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
    case 'damaged':
      return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
  }
}

export function getCoatTypeLabel(type: CoatType): string {
  const typeLabels: Record<CoatType, string> = {
    formal: 'Formal',
    casual: 'Casual',
    winter: 'Winter',
    rain: 'Rain',
    ceremonial: 'Ceremonial'
  };
  return typeLabels[type] || type;
}

export function getCoatConditionLabel(condition: CoatCondition): string {
  const conditionLabels: Record<CoatCondition, string> = {
    'new': 'New',
    'used-good': 'Used - Good',
    'worn': 'Worn',
    'requires-repair': 'Needs Repair'
  };
  return conditionLabels[condition] || condition;
}

export function getCoatSizeLabel(size: CoatSize): string {
  return size;
}

export function isCoatAvailable(coat: Coat): boolean {
  return coat.status === 'available';
}

// Date utilities

export function formatDate(date: Date | string): string {
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export function formatDateTime(date: Date | string): string {
  return new Date(date).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// File utilities

export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}

export function getFileExtension(filename: string): string {
  return filename.split('.').pop()?.toLowerCase() || '';
}

// URL utilities

export function getApiUrl(path: string): string {
  const basePath = process.env.NEXT_PUBLIC_API_BASE || '';
  return `${basePath}${path.startsWith('/') ? '' : '/'}${path}`;
}

// Object utilities

export function pick<T extends object, K extends keyof T>(
  obj: T, 
  keys: K[]
): Pick<T, K> {
  return keys.reduce((acc, key) => {
    if (obj && Object.prototype.hasOwnProperty.call(obj, key)) {
      acc[key] = obj[key];
    }
    return acc;
  }, {} as Pick<T, K>);
}

export function omit<T extends object, K extends keyof T>(
  obj: T, 
  keys: K[]
): Omit<T, K> {
  const result = { ...obj };
  keys.forEach(key => delete result[key]);
  return result;
}
